import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { GiftBuilderBannersQuery } from "@/graphql/queries/onlineStore/giftBuilderBanners.graphql.types";

export type Banner = GiftBuilderBannersQuery["giftBuilderBanner"][number];

export enum SendType {
  IMMEDIATELY = "immediately",
  FUTURE = "future",
}

const INITIAL_STATE = {
  banner: null,
  recipientFirstName: "",
  recipientLastName: "",
  recipientEmail: "",
  recipientPhone: "",
  senderName: "",
  customGiftMessage: "",
  sendType: SendType.IMMEDIATELY,
  sendDate: new Date(),
} as const;

type GiftEmailCreatorFormStore = {
  banner: Banner | null;
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  recipientPhone: string;
  senderName: string;
  customGiftMessage: string;
  sendType: SendType;
  sendDate: Date;
  setBanner: (value: Banner | null) => void;
  setTextFieldsData: (textFieldsData: PersonalizedGiftTextForm) => void;
  setSendType: (sendType: SendType) => void;
  setSendDate: (date: Date) => void;
  reset: () => void;
};

export type PersonalizedGiftTextForm = Pick<
  GiftEmailCreatorFormStore,
  | "recipientFirstName"
  | "recipientLastName"
  | "recipientPhone"
  | "recipientEmail"
  | "senderName"
  | "customGiftMessage"
>;

export const useGiftEmailCreatorFormStore = create<GiftEmailCreatorFormStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      setBanner: (value) => set({ banner: value }),
      setTextFieldsData: (textFieldsData: PersonalizedGiftTextForm) =>
        set({ ...textFieldsData }),
      setSendType: (value) => set({ sendType: value }),
      setSendDate: (value) => set({ sendDate: value }),
      reset: () => set(() => ({ ...INITIAL_STATE, sendDate: new Date() })),
    }),
    {
      name: "gift-email-creator-form",
      storage: createJSONStorage(() => localStorage, {
        reviver: (key, value) => {
          // deserialize the date correctly, JSON parsing does not automatically convert these strings back into Date objects
          if (key === "sendDate" && typeof value === "string") {
            return new Date(value);
          }
          return value;
        },
      }),
    }
  )
);

export const getBanner = (state: GiftEmailCreatorFormStore) => state.banner;
export const getRecipientFirstName = (state: GiftEmailCreatorFormStore) =>
  state.recipientFirstName;
export const getRecipientLastName = (state: GiftEmailCreatorFormStore) =>
  state.recipientLastName;
export const getRecipientEmail = (state: GiftEmailCreatorFormStore) =>
  state.recipientEmail;
export const getRecipientPhone = (state: GiftEmailCreatorFormStore) =>
  state.recipientPhone;
export const getSenderName = (state: GiftEmailCreatorFormStore) =>
  state.senderName;
export const getCustomGiftMessage = (state: GiftEmailCreatorFormStore) =>
  state.customGiftMessage;
export const getSendType = (state: GiftEmailCreatorFormStore) => state.sendType;
export const getSendDate = (state: GiftEmailCreatorFormStore) => state.sendDate;

export const getSetBanner = (state: GiftEmailCreatorFormStore) =>
  state.setBanner;
export const getSetSendType = (state: GiftEmailCreatorFormStore) =>
  state.setSendType;
export const getSetSendDate = (state: GiftEmailCreatorFormStore) =>
  state.setSendDate;
export const getSetTextFieldsData = (state: GiftEmailCreatorFormStore) =>
  state.setTextFieldsData;
export const getAllGiftFormData = (state: GiftEmailCreatorFormStore) => ({
  banner: state.banner,
  recipientFirstName: state.recipientFirstName,
  recipientLastName: state.recipientLastName,
  recipientEmail: state.recipientEmail,
  recipientPhone: state.recipientPhone,
  senderName: state.senderName,
  customGiftMessage: state.customGiftMessage,
  sendType: state.sendType,
  sendDate: state.sendDate,
});

export const getReset = (state: GiftEmailCreatorFormStore) => state.reset;
